import React, { useState, useEffect } from "react";
import { ref, get, remove, update } from "firebase/database";
import { db, auth } from "../firebaseConfig";
import "./style/Devices.css";

const Devices = () => {
  const [workspaces, setWorkspaces] = useState({});
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [devices, setDevices] = useState([]);
  const [message, setMessage] = useState("");
  const [editDevice, setEditDevice] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  
  useEffect(() => {
    const fetchWorkspaces = async () => {
      const user = auth.currentUser;

      if (!user) {
        console.error("User not authenticated");
        return;
      }
      try {
        const workspacesRef = ref(db, "neoslide/workspaces");
        const snapshot = await get(workspacesRef);
        if (snapshot.exists()) {
          const workspacesArray = Object.values(snapshot.val());
          setWorkspaces(workspacesArray);
        }
      } catch (error) {
        console.error("Error fetching workspaces:", error);
      }
    };

    fetchWorkspaces();
  }, []);

  const handleSelectChange = async (e) => {
    const workspaceName = e.target.value;
    setSelectedWorkspace(workspaceName);

    setMessage("");
    setDevices([]);

    try {
      const devicesRef = ref(db, `neoslide/devices/${workspaceName}`);
      const snapshot = await get(devicesRef);

      if (snapshot.exists()) {
        const extractedDevices = Object.entries(snapshot.val()).map(([deviceId, deviceData]) => ({
          id: deviceId,
          ...deviceData,
        }));
        setDevices(extractedDevices);
      } else {
        setMessage(`No devices found for workspace: ${workspaceName}`);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      setMessage("Error fetching devices.");
    }
  };

  const handleDeleteClick = (deviceId) => {
    setDeviceToDelete(deviceId);
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWorkspace || !deviceToDelete) return;

    try {
      await remove(ref(db, `neoslide/devices/${selectedWorkspace}/${deviceToDelete}`));
      setDevices((prevDevices) => prevDevices.filter((device) => device.id !== deviceToDelete));
      setMessage("Device deleted successfully!");
    } catch (error) {
      console.error("Error deleting device:", error);
      setMessage("Error deleting device.");
    } finally {
      setShowDeletePopup(false);
      setDeviceToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeletePopup(false);
    setDeviceToDelete(null);
  };

  const handleEditClick = (device) => {
    setEditDevice(device);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEditDevice((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "status"
            ? value === ""       
              ? ""                
              : Number(value)     
            : value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedWorkspace || !editDevice) return;

    try {
      await update(ref(db, `neoslide/devices/${selectedWorkspace}/${editDevice.id}`), {
        name: editDevice.name,
        description: editDevice.description,
        status: editDevice.status,
        supportedDevice: editDevice.supportedDevice,
      });

      setDevices((prev) =>
        prev.map((device) => (device.id === editDevice.id ? { ...device, ...editDevice } : device))
      );
      alert("Device updated successfully!");
      setEditDevice(null);
    } catch (error) {
      console.error("Error updating device:", error);
      alert("Error updating device.");
    }
  };

  return (
    <div className="workspaces-container">
      <h2>Devices</h2>
      <select onChange={handleSelectChange} value={selectedWorkspace}>
        <option value="" disabled>
          Select a workspace
        </option>
        {Object.entries(workspaces).map(([key, name]) => (
          <option key={key} value={name}>
            {name}
          </option>
        ))}
      </select>
      {message && <h2 className="success-message">{message}</h2>}
      {devices.length > 0 && (
        <table className="devices-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Object ID</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id}>
                <td>{device.name || "N/A"}</td>
                <td>{device.id}</td>
                <td>
                  <button className="edit-btn" onClick={() => handleEditClick(device)}>Edit</button>
                </td>
                <td>
                  <button className="delete-btn" onClick={() => handleDeleteClick(device.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editDevice && (
        <div className="edit-popup">
          <div className="edit-content">
            <h3>Edit Device</h3>
            <label>
              Name:
              <input type="text" name="name" value={editDevice.name} onChange={handleEditChange} />
            </label>
            <label>
              Description:
              <input type="text" name="description" value={editDevice.description} onChange={handleEditChange} />
            </label>
            <label>
              MAC Address:
              <input type="text" value={editDevice.id} disabled />
            </label>
            <label>
              Cluster ID:
              <input type="text" value={editDevice.clusterId} disabled />
            </label>
            <label>
              Current App Version:
              <input type="text" value={editDevice.currentAppVersion} disabled />
            </label>
            <label>
              Device Model:
              <input type="text" value={editDevice.deviceModel} disabled />
            </label>
            <label>
              FCM ID:
              <input type="text" value={editDevice.fcmID} disabled />
            </label>
            <label>
              Last Login Time:
              <input type="text" value={editDevice.lastLoginTime} disabled />
            </label>
            <label>
              Online:
              <input type="text" value={editDevice.online ? "Yes" : "No"} disabled />
            </label>
            <label>
              Power Device ID:
              <input type="text" value={editDevice.powerDeviceId} disabled />
            </label>
            <label>
              Status:
              <input type="number" name="status" value={editDevice.status} onChange={handleEditChange} />
            </label>

            <label className="supported-devices" >
              <p>
                Supported Device:
              </p>
              <input type="checkbox" name="supportedDevice" checked={editDevice.supportedDevice} onChange={handleEditChange} />
            </label>

            <div className="buttons">
              <button className="popup__button" onClick={handleSaveEdit}>Save</button>
              <button className="popup__button-cancel" onClick={() => setEditDevice(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showDeletePopup && (
        <div className="delete-popup">
          <div className="delete-content">
            <h3>Are you sure you want to delete this device?</h3>
            <div className="buttons">
              <button className="popup__button" onClick={handleDeleteConfirm}>Yes</button>
              <button className="popup__button-cancel" onClick={handleDeleteCancel}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;